import React, { useEffect } from 'react';
import { useNavigation } from '../../components/hocs/withNavigation';
import { NavigationSpecs } from '../../states/NavigationState';
import { PageHeader } from '../../components/atoms/layout/PageHeader';
import { PageBody } from '../../components/atoms/layout/PageBody';
import { Table } from '../../../commonv3/components/organisms/tables/Table';
import { observer, useLocalStore } from 'mobx-react';
import { CourseWorkSubmissionListState, TabType } from './states/CourseWorkSubmissionListState';
import { PagedList } from '../../../admin/types/pagination';
import { ColumnDefinitions, RowClassNames } from '../../../commonv3/components/organisms/tables/types';
import { FancyDate } from '../../components/atoms/dates/FancyDate';
import { UserProfileCell } from '../../../commonv3/components/atoms/users/UserProfileCell';
import { Pager } from '../../../commonv3/components/organisms/tables/Pager';
import './courseWorkSubmissionListPage.module.scss';
import { Link } from 'react-router-dom';
import { V3Routes } from '../../routes';
import { ReviewerSelector } from '../../components/organisms/reviewer/ReviewerSelector';
import { restrictAccessForStaff } from '../../../commonv3/hooks/restrictAccessForStaff';
import { TabSelectInput } from '../../components/atoms/select/TabSeletInput';
import { TableFilterSection } from '../../../commonv3/components/organisms/tables/TableFilterSection';
import { TableFilterList } from '../../../commonv3/components/organisms/tables/TableFilterList';
import { CheckBoxSelectInput } from '../../components/molecules/forms/CheckBoxSelectInput';
import { RadioSelectInput } from '../../components/molecules/forms/RadioSelectInput';
import { Reply } from '@material-ui/icons';
import {
  CourseWorkSubmissionIndexView,
  CourseWorkSubmissionToken,
  CourseWorkSubmissionTargetType,
} from '../../types/course_work_submissions';
import { submissionCourseWorkType } from '../../components/organisms/lesson_homeworks/CourseWorkSubmissionListItem';
import { BreadCrumb } from '../breadcrumb/BreadCrumb';

type Record = CourseWorkSubmissionIndexView;

function needToReply(r: CourseWorkSubmissionIndexView) {
  return r.unconfirmed_user_comment_count > 0 ? true : false;
}
function reviewCompleted(r: CourseWorkSubmissionIndexView) {
  if (r.review_completed_at !== null && !needToReply(r)) {
    return true;
  }
  return false;
}

export const SubmissionlinkForReviewer = (
  course_work_submission_type: CourseWorkSubmissionTargetType,
  token: CourseWorkSubmissionToken
) => {
  if (course_work_submission_type === CourseWorkSubmissionTargetType.LESSON_HOMEWORK_SUBMISSION) {
    return V3Routes.lessonHomeworkSubmissionShowForReviewer(token);
  }
  if (course_work_submission_type === CourseWorkSubmissionTargetType.EXAM_PRACTICAL_SECTION_SUBMISSION) {
    return V3Routes.examPracticalSectionSubmissionShowForReviewer(token);
  }
};

export const SubmissionlinkForUser = (
  course_work_submission_type: CourseWorkSubmissionTargetType,
  token: CourseWorkSubmissionToken
) => {
  if (course_work_submission_type === CourseWorkSubmissionTargetType.LESSON_HOMEWORK_SUBMISSION) {
    return V3Routes.lessonHomeworkSubmissionShow(token);
  }
  if (course_work_submission_type === CourseWorkSubmissionTargetType.EXAM_PRACTICAL_SECTION_SUBMISSION) {
    return V3Routes.examPracticalSectionSubmissionShow(token);
  }
};

export const CourseWorkSubmissionListPage = observer(() => {
  restrictAccessForStaff();
  useNavigation(NavigationSpecs.side());
  const { pageState } = useLocalStore(() => {
    return {
      pageState: new CourseWorkSubmissionListState(),
    };
  });
  useEffect(() => {
    pageState.init();
  }, []);
  const {
    setPage,
    submissions,
    mainTab,
    setMainTab,
    reviewableCourses,
    selectedCourseIds,
    setSelectedCourseIds,
    maxDaysBeforeDeadline,
    setMaxDaysBeforeDeadline,
  } = pageState;
  const list = submissions.getOrElse(PagedList.empty());
  const columns = [
    ColumnDefinitions.custom<Record>('提出日', (r) => (
      <div styleName="submission-date-cell">
        <FancyDate date={r.created_at} />
      </div>
    )),
    ColumnDefinitions.custom<Record>('期限', (r) => (
      <div styleName="deadline-cell">
        <FancyDate date={r.review_deadline} />
      </div>
    )),
    ColumnDefinitions.custom<Record>('残日数', (r) => {
      if (reviewCompleted(r)) {
        return '-';
      } else {
        return <span>{r.remaining_review_days}</span>;
      }
    }),
    ColumnDefinitions.custom<Record>('提出者', (r) => (
      <div styleName="submission-user-cell">
        <UserProfileCell user={r.user} />
        {needToReply(r) && <Reply styleName="reply-icon" className="reply-icon" />}
      </div>
    )),
    ColumnDefinitions.custom<Record>('コース', (r) => (
      <div styleName="course-name-cell">{r.course_work.course ? r.course_work.course.title : ''}</div>
    )),
    ColumnDefinitions.custom<Record>('種類', (r) => {
      if (r.course_work_submission_type === CourseWorkSubmissionTargetType.LESSON_HOMEWORK_SUBMISSION) {
        return <span>{r.course_work && r.course_work.lesson ? `LESSON ${r.course_work.lesson.index + 1}` : ''}</span>;
      }
      if (r.course_work_submission_type === CourseWorkSubmissionTargetType.EXAM_PRACTICAL_SECTION_SUBMISSION) {
        return <span>実技試験</span>;
      }
    }),
    ColumnDefinitions.custom<Record>('課題名', (r) => {
      const linkForReviewer = SubmissionlinkForReviewer(r.course_work_submission_type, r.token);
      return (
        <div styleName="lesson-homework-submission-cell">
          <Link to={linkForReviewer} className="homework-title">
            {r.course_work && r.course_work.title}
          </Link>
        </div>
      );
    }),
    ColumnDefinitions.custom<Record>('担当者', (r) => (
      <ReviewerSelector
        reviewer={r.reviewer}
        reviewerSkillTypeId={r.course_work.course.reviewer_skill_type_id}
        onReviewerChange={(reviewerId) => pageState.assignReviewer(r, reviewerId)}
      />
    )),
  ];
  const breadCrumbItems = [{ text: '課題添削' }];
  return (
    <div styleName="page" id="course-work-submission-list-page">
      <div styleName="page-inner">
        <BreadCrumb items={breadCrumbItems} hasHome />
        <PageHeader title="HOMEWORK" testId="page-header-of-reviewer" />
        <PageBody styleName="body">
          <TabSelectInput<TabType>
            id="main-tab"
            options={[
              {
                value: TabType.MINE_UNCOMPLETED,
                label: '自分の担当',
                metaAttributes: { 'data-tab-value': 'mine_uncompleted' },
              },
              {
                value: TabType.MINE_COMPLETED,
                label: '対応済み',
                metaAttributes: { 'data-tab-value': 'mine_completed' },
              },
              { value: TabType.ALL, label: '全体', metaAttributes: { 'data-tab-value': 'all' } },
            ]}
            value={mainTab.get() as TabType}
            onChange={setMainTab}
          />
          <TableFilterList>
            <TableFilterSection label="コース">
              <CheckBoxSelectInput
                id="course-id-selector"
                color="primary"
                options={reviewableCourses
                  .getOrElse([])
                  .map((c) => ({ label: c.title, value: c.id, valueForAttribute: c.id }))}
                values={selectedCourseIds.get()}
                onChange={setSelectedCourseIds}
              />
            </TableFilterSection>
            <TableFilterSection label="期限">
              <RadioSelectInput
                id="max-days-before-deadline-selector"
                styleName="deadline-selector"
                options={[
                  { label: '全て', value: -1, metaAttributes: { 'data-value': '-1' } },
                  { label: '期限切れ', value: 0, metaAttributes: { 'data-value': '0' } },
                  { label: '3日以内', value: 3, metaAttributes: { 'data-value': '3' } },
                  { label: '1週間以内', value: 7, metaAttributes: { 'data-value': '7' } },
                  { label: '2週間以内', value: 14, metaAttributes: { 'data-value': '14' } },
                ]}
                value={maxDaysBeforeDeadline.get()}
                onChange={(v) => setMaxDaysBeforeDeadline(v)}
              />
            </TableFilterSection>
          </TableFilterList>
          <Table<CourseWorkSubmissionIndexView>
            columns={columns}
            loading={submissions.loading}
            data={list.entries}
            rowKey={(r) => `${submissionCourseWorkType(r)}-${r.id}`}
            rowClassNames={RowClassNames.compose(
              // ストライプ模様
              RowClassNames.stripe(),
              // 期限の3日以内に近づいたら赤字でハイライト
              RowClassNames.dangerWhen<Record>((r) => r.alert_target),
              RowClassNames.grayOutWhen<Record>((r) => reviewCompleted(r))
            )}
          />
          <Pager list={list} onPageUpdate={setPage} />
        </PageBody>
      </div>
    </div>
  );
});
